import { emit } from '../../events';
import { noteUserGesture } from '../activeAdapter';
import {
  dwellProgress,
  idleDwell,
  reduceDwell,
  type DwellConfig,
  type DwellEvent,
  type DwellState,
} from '../dwellMachine';
import { correctPoint, type Point } from '../hitTest';
import type { InputAdapter, InputCapabilities, Intent } from '../InputAdapter';

/**
 * Dwell: press, hold in place, and it selects (3.1).
 *
 * Plumbing only. Every rule lives in `dwellMachine.ts`, tested as a reducer, because the interesting
 * part is timing and timing through a browser is timing through a scheduler nobody controls. What this
 * file owns is pointer events, the animation frame that drives the ring, and the CSS variables the
 * indicator reads.
 *
 * ── `isTimed: true` is the most consequential line here ──
 *
 * A dwell completion is **not** a Safari user gesture. `activeAdapter.inGesture()` already returns
 * false for a timed adapter and `WebSpeech.isAvailable` already honours it, so the chain will skip the
 * platform voice on its own — Phase 1 built that seam for exactly this moment (C18). Getting this flag
 * wrong would not throw: the person would tap, see their sentence, and never be heard. Which is why
 * 3.2 exists, and why `useTapToSpeak` is part of this stage rather than a later polish.
 */

const CAPABILITIES: InputCapabilities = {
  /** The dwell ring *is* the focus indication, and it needs `focusRingPx` to draw itself. */
  needsFocusRing: true,
  isTimed: true,
  /** Completion deserves a distinct sound as well as a distinct look (3.1). */
  needsAudioCue: true,
};

export interface DwellSettings {
  dwellMs: number;
  slopPx: number;
  repeatSuppressMs: number;
  keyRepeatSuppressMs: number;
  touchOffsetCorrection: Point;
}

/**
 * Tremor debounce per target class, the same asymmetry `DirectTouch` documents.
 *
 * A suppressed letter makes German double letters untypeable and says nothing about why; a suppressed
 * `Löschen` merely costs a second attempt. So keys get the short window.
 */
function suppressWindowFor(targetId: string, settings: DwellSettings): number {
  return targetId.startsWith('key:') ? settings.keyRepeatSuppressMs : settings.repeatSuppressMs;
}

export function createDwellAdapter(getSettings: () => DwellSettings): InputAdapter {
  let root: HTMLElement | null = null;
  let emitIntent: ((intent: Intent) => void) | null = null;

  let state: DwellState = idleDwell();
  let pressTargetEl: HTMLElement | null = null;
  let pressAtMs = 0;
  let lastTargetId: string | null = null;
  let lastAtMs: number | null = null;
  let frame: number | null = null;
  /** The pointer that started the press. Others are ignored — a braced hand must not steal it. */
  let pointerId: number | null = null;

  const configFor = (targetId: string): DwellConfig => {
    const settings = getSettings();
    return {
      dwellMs: settings.dwellMs,
      slopPx: settings.slopPx,
      repeatSuppressMs: suppressWindowFor(targetId, settings),
    };
  };

  const targetFrom = (event: PointerEvent): HTMLElement | null => {
    const el = event.target;
    if (!(el instanceof Element)) return null;
    return el.closest<HTMLElement>('[data-activate-id]');
  };

  /** The indicator reads these; nothing else does. Cleared whenever a press ends, however it ends. */
  const paint = (progress: number): void => {
    if (!root) return;
    root.style.setProperty('--dwell-progress', String(progress));
    if (pressTargetEl) pressTargetEl.dataset.dwelling = 'true';
  };

  const clearPaint = (): void => {
    if (root) root.style.setProperty('--dwell-progress', '0');
    if (pressTargetEl) delete pressTargetEl.dataset.dwelling;
  };

  const step = (targetId: string, event: DwellEvent): void => {
    const result = reduceDwell(state, event, configFor(targetId), { lastTargetId, lastAtMs });
    state = result.state;

    const effect = result.effect;
    if (!effect) return;

    switch (effect.kind) {
      case 'activate': {
        const atMs = performance.now();
        lastTargetId = effect.targetId;
        lastAtMs = atMs;
        clearPaint();

        emit({
          type: 'KeyActivated',
          key: effect.targetId,
          // Press to completion, which for dwell is `dwellMs` plus whatever the frame loop cost. It is
          // what the person waited, so it is what gets recorded.
          latencyMs: Math.round(atMs - pressAtMs),
          position: { x: 0, y: 0 },
        });
        emitIntent?.({ kind: 'activate', targetId: effect.targetId });
        break;
      }

      case 'cancelled': {
        clearPaint();
        // Recorded, because a rising cancellation rate is how "dwellMs is too long today" becomes
        // visible to a caregiver who cannot see it any other way. `DirectTouch` records misses for the
        // same reason — a device that only logs successes cannot report a mode that is not working.
        emit({ type: 'DwellCancelled', key: effect.targetId, reason: effect.reason });
        break;
      }

      case 'suppressed': {
        clearPaint();
        // Silent: the person meant one selection and a tremor produced two contacts.
        break;
      }
    }
  };

  const loop = (): void => {
    if (state.kind === 'dwelling') {
      const { dwellMs } = getSettings();
      paint(dwellProgress(state, performance.now(), dwellMs));
      step(state.targetId, { kind: 'tick', atMs: performance.now() });
    }
    frame = state.kind === 'dwelling' ? requestAnimationFrame(loop) : null;
  };

  const onPointerDown = (event: PointerEvent): void => {
    // A real gesture: the *press* is one even though the completion is not. Recorded so that anything
    // triggered by the press itself — audio unlock, in particular — still counts.
    noteUserGesture();

    if (pointerId !== null) return;
    pointerId = event.pointerId;

    const target = targetFrom(event);
    const rect = target?.getBoundingClientRect() ?? null;
    const settings = getSettings();
    pressTargetEl = target;
    pressAtMs = performance.now();

    step(target?.dataset.activateId ?? '', {
      kind: 'down',
      targetId: target?.dataset.activateId ?? null,
      rect: rect ? { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom } : null,
      point: correctPoint({ x: event.clientX, y: event.clientY }, settings.touchOffsetCorrection),
      atMs: pressAtMs,
    });

    if (state.kind === 'dwelling' && frame === null) frame = requestAnimationFrame(loop);
  };

  const onPointerMove = (event: PointerEvent): void => {
    if (event.pointerId !== pointerId || state.kind !== 'dwelling') return;
    const settings = getSettings();

    step(state.targetId, {
      kind: 'move',
      point: correctPoint({ x: event.clientX, y: event.clientY }, settings.touchOffsetCorrection),
      atMs: performance.now(),
    });
  };

  const endPress = (event: PointerEvent): void => {
    if (event.pointerId !== pointerId) return;
    pointerId = null;

    const targetId = state.kind === 'dwelling' ? state.targetId : '';
    step(targetId, { kind: 'up', atMs: performance.now() });

    clearPaint();
    pressTargetEl = null;
    if (frame !== null) {
      cancelAnimationFrame(frame);
      frame = null;
    }
  };

  return {
    id: 'dwell',
    capabilities: CAPABILITIES,

    attach(element: HTMLElement, emitFn: (intent: Intent) => void): void {
      root = element;
      emitIntent = emitFn;
      root.addEventListener('pointerdown', onPointerDown);
      root.addEventListener('pointermove', onPointerMove);
      root.addEventListener('pointerup', endPress);
      root.addEventListener('pointercancel', endPress);
    },

    detach(): void {
      root?.removeEventListener('pointerdown', onPointerDown);
      root?.removeEventListener('pointermove', onPointerMove);
      root?.removeEventListener('pointerup', endPress);
      root?.removeEventListener('pointercancel', endPress);
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
      clearPaint();
      state = idleDwell();
      pointerId = null;
      pressTargetEl = null;
      root = null;
      emitIntent = null;
    },
  };
}
